import { FiX, FiSettings } from "react-icons/fi";
import { FiThermometer } from "react-icons/fi";
import { TbGauge } from "react-icons/tb";
import Area from "components/charts/Area/Area";
import areaApiConfig from "config/charts/areaApiConfig";
import Gauge from "components/charts/Gauge/Gauge";
import Card from "../shared/Card/Card";
import { useState, useEffect, useContext } from "react";
import { ModalContext } from "hooks/ModalProvider";
import { Item } from "types/common";
import { getTagData } from "api/tags";
import classNames from "utilities/ClassNames";

const ServerModal = ({
  onClose,
  onEdit,
  title,
  mode = "generic",
}: {
  onClose?: any;
  onEdit?: any;
  title?: string;
  mode?: "generic" | "unity";
}) => {
  const rackSlots: Array<{
    label: string;
    height: number;
    backgroundColor?: string;
    textColor?: string;
  }> = [
    { label: "", height: 2 },
    {
      label: "Storage — Dell EMC Isilon H400 JWXNC181400370 — LAN",
      height: 4,
      backgroundColor: "#2E968A",
    },
    { label: "", height: 1 },
    {
      label: "Computing — Dell PowerEdge R640 R640-6H19Q2 — LAN",
      height: 1,
      backgroundColor: "#7ED34E",
    },
    {
      label: "Computing — Cisco UCS C220 M5 UCSC-C220M5-8G4R2P — LAN",
      height: 1,
      backgroundColor: "#7ED34E",
    },
    {
      label: "Computing — Cisco SNS 3595 Appliance WMP2407004E — LAN",
      height: 1,
      backgroundColor: "#7ED34E",
    },
    { label: "", height: 1 },
    {
      label: "Network — Palo Alto Networks PA-220 013201022771 — LAN",
      height: 1,
      backgroundColor: "#FF5233",
    },
    {
      label: "Network — Palo Alto Networks PA-5220 13201021987 — LAN",
      height: 2,
      backgroundColor: "#FF5233",
    },
    {
      label: "Network — Palo Alto Networks PA-5220 13201021190 — LAN",
      height: 3,
      backgroundColor: "#FF5233",
    },
    { label: "", height: 1 },
    {
      label: "Computing — Cisco AMP PC3000 WMP2406004X — LAN",
      height: 2,
      backgroundColor: "#7ED34E",
    },
    { label: "", height: 22 },
  ];

  const totalURack = 42;
  const usedU = rackSlots.reduce((sum, slot) => sum + slot.height, 0);
  const freeU = Math.max(totalURack - usedU, 0);

  const mainTabs = [
    {
      id: "installedDevices",
      label: "Installed Devices",
    },
    {
      id: "rackPDU",
      label: "Rack PDU",
    },
  ];

  const installedDevicesTabs = [
    {
      id: "genInfo",
      label: "General Information",
      content: null,
    },
    {
      id: "status",
      label: "Status & Metrics",
      content: null,
    },
    {
      id: "connectivity",
      label: "Connectivity",
      content: null,
    },
  ];

  const rackPDUTabs = [
    {
      id: "pduOverview",
      label: "PDU Overview",
    },
    {
      id: "bankLoads",
      label: "Bank Loads",
    },
  ];

  const tableTabs = [
    {
      id: "graph",
      label: "Graph",
      content: null,
    },
    {
      id: "alarm",
      label: "Alarm List",
      content: null,
    },
  ];

  const [randomTag, setRandomTag] = useState<Item | null>(null);
  const [selectedMainTab, setSelectedMainTab] = useState("installedDevices");
  const [selectedInstalledDevicesTab, setSelectedInstalledDevicesTab] = useState("genInfo");
  const [selectedRackPDUTab, setSelectedRackPDUTab] = useState("pduOverview");
  const [selectedTableTab, setSelectedTableTab] = useState("graph");
  const [serverTableData, setServerTableData] = useState<any[]>([]);
  const [graphName, setGraphName] = useState("Power Consumption");
  const [selectedSlot, setSelectedSlot] = useState("Server");
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const { setServersModalShown } = useContext(ModalContext);

  const serverData = [
    { label: "Device name", value: `A5-SD-0434` },
    { label: "Type", value: `Rack` },
    { label: "Model HP", value: "NetShelter SX 42U 750mm Wide" },
    { label: "Serial Number", value: "AR3150-SN-A5SD0434" },
    { label: "Vendor", value: "APC" },
    { label: "U-Position", value: "U1–U42" },
    { label: "Device Asset Tag/CMDB ID", value: "A5-SD-0434" },
    {
      label: "Location",
      value: "Row-A-SD / NIC SITE-1 / SUPERDOME / Site 1-Riyad",
    },
    { label: "Owner", value: "SDAIA" },
  ];

  const pduOverviewData = [
    { label: "Device Name", value: "ROW Y R01-B" },
    { label: "Model", value: "AP8856" },
    { label: "Status", value: "ONLINE" },
    { label: "IP Address", value: "10.21.37.211" },
    { label: "Input Phases", value: "3" },
    { label: "Bank Count", value: "6" },
    { label: "Last Update", value: "2026-01-19 14:32" },
  ];

  const bankLoadsData = [
    { bank: "Bank 1", current: "8.1" },
    { bank: "Bank 2", current: "12.6" },
    { bank: "Bank 3", current: "6.0" },
    { bank: "Bank 4", current: "9.2" },
    { bank: "Bank 5", current: "7.4" },
    { bank: "Bank 6", current: "5.8" },
  ];

  const getSingleTag = async () => {
    const randomTagData = await getTagData([244]);
    setRandomTag(randomTagData.data[0]);
  };

  useEffect(() => {
    getSingleTag();
  }, []);

  useEffect(() => {
    setServerTableData(serverData);
  }, [graphName, selectedSlot]);

  useEffect(() => {
    if (selectedMainTab === "installedDevices") {
      if (selectedInstalledDevicesTab === "genInfo") {
        setServerTableData(serverData);
      } else {
        setServerTableData([]);
      }
    } else if (selectedMainTab === "rackPDU") {
      if (selectedRackPDUTab === "pduOverview") {
        setServerTableData(pduOverviewData);
      } else if (selectedRackPDUTab === "bankLoads") {
        setServerTableData(bankLoadsData);
      }
    }
  }, [selectedMainTab, selectedInstalledDevicesTab, selectedRackPDUTab]);

  const [slotHeight, setSlotHeight] = useState(17);

  useEffect(() => {
    const updateHeight = () => {
      setSlotHeight(window.innerWidth < 1750 ? 17 : 18);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Handle window resize for Gauge components
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      className="h-screen w-screen bg-black/65 flex items-center justify-center fixed left-0 top-0 z-50"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="h-[97%] w-[1200px] xl-1:w-[1270px] xl-2:w-[1300px] xl-2xl:w-[1350px] 2xl:w-[1400px] bg-black px-3 sm:py-2 2xl:py-2 flex flex-col gap-2 overflow-hidden">
        <div className="bg-dark/85 relative w-full p-1 px-2 rounded flex h-max">
          <h2 className="w-full text-center">{title ? title : "Rack-1"}</h2>
          <div className="absolute right-2 h-full top-0 flex items-center justify-center">
            <div className="flex gap-2">
              <button
                className="cursor-pointer bg-blue rounded-md p-0.5"
                onClick={() => {
                  setServersModalShown(false);
                  if (onClose) {
                    onClose();
                  }
                }}
              >
                <FiX size={20} />
              </button>
              {mode === "unity" && onEdit && (
                <button
                  className="cursor-pointer bg-blue rounded-md p-0.5"
                  onClick={() => onEdit()}
                >
                  <FiSettings size={20} />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 w-full flex-1 overflow-hidden">
          {/* Rack view */}
          <div className="overflow-y-scroll h-full max-h-full w-1/2 flex flex-col items-center justify-center rounded-md py-1 bg-dark relative">
            <div className="absolute top-0 right-0 w-[110px] xl-1:w-[125px] xl-2:w-[135px] xl-2xl:w-[150px] 2xl:w-[150px] h-full py-28 2xl:pr-2 z-50">
              <div className="w-full h-full py-14 flex flex-col justify-between">
                <div className="w-full h-32">
                  <div className="w-full h-5 pr-7">
                    <div className="w-full h-5 border-r-2 border-t-2 border-white/65 rounded-tr-md relative">
                      <div className="absolute w-2 h-2 bg-white rounded-full z-30 left-0 -top-1" />
                    </div>
                  </div>
                  <div className="w-full h-full flex flex-col items-end justify-end rounded-md">
                    <div className="h-[50%] border-2 border-white/65 w-16 flex items-center justify-center gap-1 rounded-t-md bg-[#051928]">
                      <TbGauge size={18} />
                      <span>43%</span>
                    </div>
                    <div className="h-[50%] border-2 border-white/65 w-16 flex items-center justify-center gap-1 rounded-b-md bg-[#051928]">
                      <FiThermometer size={18} />
                      <span>16C</span>
                    </div>
                  </div>
                </div>
                <div className="w-full h-32">
                  <div className="w-full h-full flex flex-col items-end justify-end rounded-md ">
                    <div className="h-[50%] border-2 border-white/65 w-16 flex items-center justify-center gap-1 rounded-t-md bg-[#051928]">
                      <TbGauge size={18} />
                      <span>43%</span>
                    </div>
                    <div className="h-[50%] border-2 border-white/65 w-16 flex items-center justify-center gap-1 rounded-b-md bg-[#051928]">
                      <FiThermometer size={18} />
                      <span>16C</span>
                    </div>
                  </div>
                  <div className="w-full h-5 pr-7">
                    <div className="relative w-full h-5 border-r-2 border-b-2 rounded-br-md border-white/65">
                      <div className="absolute w-2 h-2 bg-white rounded-full z-30 left-0 -bottom-1" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex h-max relative z-30">
              <div className="h-full flex flex-col items-center justify-start rounded-xl min-w-max">
                <div className="w-[425px] 2xl:w-[450px] h-[20px] bg-[#000]/50 rounded-t-xl" />
                <div className="relative w-[425px] 2xl:w-[450px] perspective-1000 flex h-max">
                  {/* Left rail with holes */}
                  <div className="w-6 h-full bg-[#000]/50 flex flex-col justify-between items-center py-2">
                    {Array.from({ length: totalURack }).map((_, idx) => (
                      <div
                        key={idx}
                        className="w-1 h-1 rounded-full bg-white/70"
                      />
                    ))}
                  </div>
                  {/* Rack body */}
                  <div className="w-full bg-[#051928]">
                    <div className="border rounded-xl border-slate/50 bg-gradient-to-b from-[#000]/55 via-[#000]/35 to-[#000]/35 rounded-b-md overflow-hidden">
                      {rackSlots
                        .slice()
                        .reverse()
                        .map((slot, i) => {
                          const baseClasses =
                            "w-full text-[11px] font-medium flex items-center justify-between px-2 rounded-md";

                          const isEmpty = slot.label === "";

                          // Helper function to convert hex to rgba with 65% opacity
                          const hexToRgba = (
                            hex: string,
                            opacity: number = 0.65,
                          ): string => {
                            // Remove # if present
                            const cleanHex = hex.replace("#", "");
                            // Parse hex to RGB
                            const r = parseInt(cleanHex.substring(0, 2), 16);
                            const g = parseInt(cleanHex.substring(2, 4), 16);
                            const b = parseInt(cleanHex.substring(4, 6), 16);
                            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
                          };

                          // Determine background color classes or styles
                          const backgroundStyle: React.CSSProperties = {};
                          let backgroundClasses = "border border-gray";

                          if (isEmpty) {
                            backgroundClasses = "bg-[#05070b]";
                          } else if (slot.backgroundColor) {
                            // Check if it's a hex color or inline style
                            if (slot.backgroundColor.startsWith("#")) {
                              // Convert hex to rgba with 65% opacity
                              backgroundStyle.backgroundColor = hexToRgba(
                                slot.backgroundColor,
                                0.65,
                              );
                            } else if (slot.backgroundColor.startsWith("rgb")) {
                              // If it's already rgba, extract and set opacity to 0.65
                              const rgbMatch =
                                slot.backgroundColor.match(/\d+/g);
                              if (rgbMatch && rgbMatch.length >= 3) {
                                backgroundStyle.backgroundColor = `rgba(${rgbMatch[0]}, ${rgbMatch[1]}, ${rgbMatch[2]}, 0.65)`;
                              } else {
                                backgroundStyle.backgroundColor =
                                  slot.backgroundColor;
                              }
                            } else if (
                              slot.backgroundColor.startsWith("from-") ||
                              slot.backgroundColor.startsWith("bg-")
                            ) {
                              // It's a Tailwind class
                              if (slot.backgroundColor.startsWith("from-")) {
                                backgroundClasses = `bg-gradient-to-r ${slot.backgroundColor}`;
                              } else {
                                backgroundClasses = slot.backgroundColor;
                              }
                            } else {
                              // Assume it's a Tailwind gradient string
                              backgroundClasses = `bg-gradient-to-r ${slot.backgroundColor}`;
                            }
                          } else {
                            // Fallback to default logic based on label
                            if (slot.label === "Firewall") {
                              backgroundClasses =
                                "bg-gradient-to-r from-red to-red/70";
                            } else if (slot.label === "Router") {
                              backgroundClasses =
                                "bg-gradient-to-r from-blue to-blue";
                            } else if (slot.label === "Switch") {
                              backgroundClasses =
                                "bg-gradient-to-r from-[#373d7e] to-[#373d7e]/70";
                            } else if (slot.label === "Network Balancer") {
                              backgroundClasses =
                                "bg-gradient-to-r from-green to-green/70";
                            } else if (slot.label === "Server") {
                              backgroundClasses =
                                "bg-gradient-to-b from-[#1f2937] to-[#030712]";
                            } else {
                              backgroundClasses =
                                "bg-gradient-to-b from-slate-800 to-slate-900";
                            }
                          }

                          // Determine text color classes or styles
                          const textStyle: React.CSSProperties = {};
                          let textClasses = "";

                          if (slot.textColor) {
                            if (
                              slot.textColor.startsWith("#") ||
                              slot.textColor.startsWith("rgb")
                            ) {
                              textStyle.color = slot.textColor;
                            } else if (slot.textColor.startsWith("text-")) {
                              textClasses = slot.textColor;
                            } else {
                              textClasses = `text-${slot.textColor}`;
                            }
                          } else {
                            // Fallback to default text colors
                            if (isEmpty) {
                              textClasses = "text-gray-300";
                            } else if (slot.label === "Server") {
                              textClasses = "text-slate-100";
                            } else {
                              textClasses = "text-white";
                            }
                          }

                          return (
                            <div
                              key={i}
                              className={classNames(
                                baseClasses,
                                isEmpty ? "bg-[#000]/05" : backgroundClasses,
                                textClasses,
                                slot.height > 1 ? "shadow-lg" : "",
                              )}
                              style={{
                                height: `${slot.height * slotHeight}px`,
                                ...backgroundStyle,
                                ...textStyle,
                              }}
                              onClick={() =>
                                !isEmpty && setSelectedSlot(slot.label)
                              }
                            >
                              <span className="text-[10px] 2xl:text-[12px] truncate">
                                {slot.label || ""}
                              </span>
                              {!isEmpty && (
                                <span className="flex gap-0.5">
                                  <span className="w-1 h-1 rounded-sm bg-white/70" />
                                  <span className="w-1 h-1 rounded-sm bg-white/40" />
                                  <span className="w-1 h-1 rounded-sm bg-white/20" />
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                  {/* Right rail with holes */}
                  <div className="w-6 h-full bg-[#000]/50 flex flex-col justify-between items-center py-2">
                    {Array.from({ length: totalURack }).map((_, idx) => (
                      <div
                        key={idx}
                        className="w-1 h-1 rounded-full bg-white/70"
                      />
                    ))}
                  </div>
                </div>
                <div className="w-[425px] 2xl:w-[450px] h-[25px] flex flex-col items-center justify-start">
                  <div className="w-full h-[15px] bg-[#000]/50 shadow-[0_6px_10px_rgba(0,0,0,0.7)]" />
                  <div className="w-full h-[10px] px-6 flex justify-between">
                    <div className="w-6 h-full bg-[#000]/50" />
                    <div className="w-6 h-full bg-[#000]/50" />
                  </div>
                </div>
              </div>
              <div className="w-6 pt-[18px] flex items-start justify-center">
                <div className="bg-black rounded-r-md pt-1">
                  <div className="w-6"></div>
                  <div
                    className="flex-1 flex flex-col justify-between px-2"
                    style={{ height: `${totalURack * slotHeight}px` }}
                  >
                    {Array.from({ length: totalURack }).map((_, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-center"
                        style={{ height: `${slotHeight}px` }}
                      >
                        <span className="text-[8px] text-white/60 font-medium">
                          {totalURack - idx}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="w-6"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="h-full w-1/2 bg-dark rounded-md overflow-hidden">
            <div className="flex flex-col gap-2 h-full py-2 px-2 overflow-hidden">
              {randomTag && (
                <div className="flex gap-4 w-full items-center">
                  <div className="w-[30%] flex flex-col items-center gap-2">
                    
                    <div className="w-full flex justify-center">
                      <Gauge
                        data={{ ...randomTag, name: "" }}
                        gaugeType={"regular"}
                      />
                    </div>
                    <div className="text-center text-base 2xl:text-lg font-semibold text-white">
                      Total Rack Power
                    </div>
                  </div>
                  
                  <div className="flex-1 flex flex-col gap-2 mt-3">
                    <div className="flex gap-2">
                      <Card title="L1" className="flex-1">
                        <div className="p-2 h-full w-full flex flex-col items-center justify-center">
                          <div className="text-2xl 2xl:text-3xl text-green">99.2</div>
                          <div className="text-xs 2xl:text-sm text-white/70">kW</div>
                        </div>
                      </Card>

                      <Card title="L2" className="flex-1">
                        <div className="p-2 h-full w-full flex flex-col items-center justify-center">
                          <div className="text-2xl 2xl:text-3xl text-green">99.8</div>
                          <div className="text-xs 2xl:text-sm text-white/70">kW</div>
                        </div>
                      </Card>

                      <Card title="L3" className="flex-1">
                        <div className="p-2 h-full w-full flex flex-col items-center justify-center">
                          <div className="text-2xl 2xl:text-3xl text-green">99.1</div>
                          <div className="text-xs 2xl:text-sm text-white/70">kW</div>
                        </div>
                      </Card>
                    </div>
                    <div className="text-center text-sm 2xl:text-base font-semibold text-white/90">
                      Phase Power Distribution
                    </div>
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-hidden min-h-0 flex flex-col gap-2">
                {/* Main Tabs */}
                <div className="flex gap-2">
                  {mainTabs.map((tab) =>
                    <div onClick={() => setSelectedMainTab(tab.id)} key={tab.id} className={`px-4 py-1.5 bg-[#373d7e] rounded-md hover:bg-gray-100 cursor-pointer transition-colors ${selectedMainTab === tab.id ? 'bg-gradient-to-r from-[#7ED34E] to-[#2E968A] text-white' : ''}`}>
                      <h3 className="text-sm 2xl:text-base font-semibold">{tab.label}</h3>
                    </div>
                  )}
                </div>

                {/* Installed Devices Content */}
                {selectedMainTab === "installedDevices" && (
                  <div className="flex-1 overflow-hidden min-h-0">
                    <div className="py-2 bg-black rounded-lg px-4 flex flex-col overflow-hidden h-full">
                      <div className="flex gap-2 mb-2">
                        {installedDevicesTabs.map((tab) =>
                          <div onClick={() => setSelectedInstalledDevicesTab(tab.id)} key={tab.id} className={`px-3 py-1 bg-[#373d7e] rounded-md hover:bg-gray-100 cursor-pointer transition-colors ${selectedInstalledDevicesTab === tab.id ? 'bg-gradient-to-r from-[#7ED34E] to-[#2E968A] text-white' : ''}`}>
                            <h3 className="text-xs 2xl:text-sm font-medium">{tab.label}</h3>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto">
                        <table className="w-full">
                          <tbody className="w-full">
                            {serverTableData && serverTableData.map(({ label, value }, index) => (
                              <tr
                                key={label}
                                className={`w-full ${index !== serverTableData.length - 1 ? "border-b border-[#3b5c74]" : ""}`}
                              >
                                <td className="py-1 2xl:py-2 px-0 text-xs 2xl:text-sm font-normal text-white/70">{label}</td>
                                <td className="py-1 2xl:py-2 px-0 text-end text-xs 2xl:text-sm font-normal text-white">{value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rack PDU Content */}
                {selectedMainTab === "rackPDU" && (
                  <div className="flex-1 overflow-hidden min-h-0">
                    <div className="py-2 bg-black rounded-lg px-4 flex flex-col overflow-hidden h-full">
                      <div className="flex gap-2 mb-2">
                        {rackPDUTabs.map((tab) =>
                          <div onClick={() => setSelectedRackPDUTab(tab.id)} key={tab.id} className={`px-3 py-1 bg-[#373d7e] rounded-md hover:bg-gray-100 cursor-pointer transition-colors ${selectedRackPDUTab === tab.id ? 'bg-gradient-to-r from-[#7ED34E] to-[#2E968A] text-white' : ''}`}>
                            <h3 className="text-xs 2xl:text-sm font-medium">{tab.label}</h3>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto">
                        {selectedRackPDUTab === "pduOverview" && (
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="border-2 border-[#4a5f6f]">
                                <th className="py-3 px-4 text-left text-sm 2xl:text-base font-semibold text-white border-r-2 border-[#4a5f6f] bg-[#1a2f3f]">Parameter</th>
                                <th className="py-3 px-4 text-left text-sm 2xl:text-base font-semibold text-white bg-[#1a2f3f]">Value</th>
                              </tr>
                            </thead>
                            <tbody className="w-full">
                              {serverTableData && serverTableData.map(({ label, value }, index) => (
                                <tr
                                  key={label}
                                  className="border-2 border-t-0 border-[#4a5f6f]"
                                >
                                  <td className="py-3 2xl:py-4 px-4 text-xs 2xl:text-sm font-normal text-white/90 border-r-2 border-[#4a5f6f]">{label}</td>
                                  <td className="py-3 2xl:py-4 px-4 text-xs 2xl:text-sm font-normal text-white">{value}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        {selectedRackPDUTab === "bankLoads" && (
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="border-2 border-[#4a5f6f]">
                                <th className="py-3 px-4 text-left text-sm 2xl:text-base font-semibold text-white border-r-2 border-[#4a5f6f] bg-[#1a2f3f]">Bank</th>
                                <th className="py-3 px-4 text-left text-sm 2xl:text-base font-semibold text-white bg-[#1a2f3f]">Current (A)</th>
                              </tr>
                            </thead>
                            <tbody className="w-full">
                              {serverTableData && serverTableData.map(({ bank, current }, index) => (
                                <tr
                                  key={bank}
                                  className="border-2 border-t-0 border-[#4a5f6f]"
                                >
                                  <td className="py-3 2xl:py-4 px-4 text-xs 2xl:text-sm font-normal text-white/90 border-r-2 border-[#4a5f6f]">{bank}</td>
                                  <td className="py-3 2xl:py-4 px-4 text-xs 2xl:text-sm font-normal text-white">{current}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {randomTag && (
                <div className="flex-1 overflow-hidden min-h-0">
                  <div className="py-2 bg-black rounded-lg px-4 overflow-hidden flex flex-col h-full">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex gap-2">
                        {tableTabs.map((tab) =>
                          <div onClick={() => setSelectedTableTab(tab.id)} key={tab.id} className={`px-3 py-1 bg-[#373d7e] rounded-md hover:bg-gray-100 cursor-pointer transition-colors ${selectedTableTab === tab.id ? 'bg-gradient-to-r from-[#7ED34E] to-[#2E968A] text-white' : ''}`}>
                            <h3 className="text-xs 2xl:text-sm font-medium">{tab.label}</h3>
                          </div>
                        )}
                      </div>
                      <h3 className="font-semibold text-sm 2xl:text-base text-white">{graphName}</h3>
                    </div>
                    {selectedTableTab === 'graph' && <div className="flex-1 overflow-hidden">
                      <Area
                        item={randomTag}
                        type="tag"
                        areaType="regular"
                        renderType="lazyLoading"
                        maintainAspectRatio={false}
                        areaApiConfig={areaApiConfig}
                      />
                    </div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServerModal;
